import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "~/lib/utils";
import { type ArtifactPreview } from "~/services/artifacts";
import { ArtifactShell } from "./ArtifactShell";
import { FileArtifactBody } from "./FileArtifactBody";
import { filenameFromPreview, hasFileSuffix, urlFromPreview } from "./inlineViewerPreview";

const HTML_SUFFIXES = [".html", ".htm"];
const MAX_HTML_FILE_BYTES = 20 * 1024 * 1024;

export function isHtmlArtifact(preview: ArtifactPreview): boolean {
  return hasFileSuffix(preview, HTML_SUFFIXES);
}

interface HtmlViewerProps {
  preview: ArtifactPreview;
  className?: string;
}

// Renders artifact HTML inside a sandboxed iframe via `srcDoc`. The sandbox
// grants `allow-scripts` (so interactive output like Plotly/Bokeh runs) but
// deliberately withholds `allow-same-origin` — the document lands in an opaque
// origin with no access to the app's cookies, storage, or DOM. This is why we
// can render agent-produced HTML without sanitizing it inline.
const HtmlViewer = ({ preview, className }: HtmlViewerProps) => {
  const [html, setHtml] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const url = urlFromPreview(preview);

  useEffect(() => {
    let cancelled = false;

    const loadHtml = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setHtml(null);

        if (!url) {
          throw new Error("No content URL available for this artifact.");
        }

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }

        const contentLength = response.headers.get("Content-Length");
        if (contentLength && Number(contentLength) > MAX_HTML_FILE_BYTES) {
          throw new Error("File is too large to render inline. Please download it instead.");
        }

        const htmlText = await response.text();
        if (cancelled) return;

        setHtml(htmlText);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load HTML artifact:", err);
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadHtml();

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 rounded-md border border-border bg-muted/40 text-sm text-muted-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className ?? "h-[450px]")}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
          <div className="text-center">
            <Loader2 className="size-8 text-muted-foreground animate-spin mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading HTML...</p>
          </div>
        </div>
      )}
      {html !== null && (
        <iframe
          title={filenameFromPreview(preview) || "HTML artifact"}
          srcDoc={html}
          sandbox="allow-scripts"
          className={cn(
            "h-full w-full rounded-md border border-border bg-white",
            isLoading && "invisible"
          )}
        />
      )}
    </div>
  );
};

export const HtmlArtifact = ({ preview }: { preview: ArtifactPreview }) => (
  <ArtifactShell
    preview={preview}
    expandedContent={<HtmlViewer preview={preview} className="h-full" />}
  >
    <FileArtifactBody preview={preview} />
    <div className="mt-3">
      <HtmlViewer preview={preview} />
    </div>
  </ArtifactShell>
);
